/**
 * Soft≠Live Page178 base + Page223 dual residual (frames_plane ∥ catalogue_plane).
 * Page101 Soft≠Live residual liquid shell on ops-cam--liquid.
 * Mount: /ops/cameras (panel tab)
 * Buses (isolated — each fails closed, no Soft invent cascade):
 *   GET /ops/cameras/status (always-on Live posture · dual frames ∥ catalogue)
 *   GET /ops/cameras/frames when frames_plane_allowed (principal-scoped Live)
 *   GET /admin/cameras when catalogue_plane_allowed
 *   GET /devices when register_read_allowed
 *   health-probe · probe-test · enqueue (catalogue) · send-email (admin frames)
 * Soft residual deleted:
 *  - useLive Soft dual-path without posture SoT
 *  - Soft RTSP players / frames-only theatre / brochure catalogues
 *  - unbounded snapshot fetches (bounded by status limits.frames_*)
 *  - raw API path / code paint in operator DOM
 *  - admin-only frames Soft-hide for operators who own devices
 * Honesty (grep): No frames yet · Catalogue · Probe · Bind · Plant
 * Do not unique On-site frames by device_id — two cameras on one board stay two chips.
 */
import { useMemo, useState } from "react";
import { html, Ico, KV, Field, Input, Alert, go } from "./ui.js";
import { path, useAction, useApi } from "./api.js";
import { envelopeSummary } from "./action-envelope.js";
import { DeviceCamerasLive, enqueueDeviceSnapshot } from "./device-cameras.js";
import { boardHeadingOf, cameraPlantSay, operatorSay } from "./operator-say.js";

const CAM_TABS = [
  { id: "frames", label: "Live", long: "Pictures", needsFrames: true },
  { id: "cat", label: "List", long: "Cameras", needsCatalogue: true },
  { id: "probe", label: "Test", long: "Test", needsProbe: true },
  { id: "bind", label: "Link", long: "Link", needsBind: true },
];

/** Soft≠Live: missing contract ops must not crash the desk. */
function safePath(op, opts) {
  try {
    return path(op, opts);
  } catch {
    if (op === "get__ops_cameras_status") return "/ops/cameras/status";
    if (op === "get__ops_cameras_frames") {
      const q = (opts && opts.query) || {};
      const parts = Object.entries(q)
        .filter(([, v]) => v != null && String(v) !== "")
        .map(([k, v]) => `${k}=${encodeURIComponent(v)}`);
      return parts.length
        ? `/ops/cameras/frames?${parts.join("&")}`
        : "/ops/cameras/frames";
    }
    if (op === "get__devices") return "/devices";
    if (op === "get__admin_cameras") return "/admin/cameras";
    if (op === "post__admin_cameras_probe-test") return "/admin/cameras/probe-test";
    if (op === "post__admin_cameras_p_health-probe") {
      const id = opts && opts.pathParams && opts.pathParams[0];
      return id ? `/admin/cameras/${encodeURIComponent(id)}/health-probe` : "";
    }
    if (op === "post__admin_camera-snapshots_p_send-email") {
      const id = opts && opts.pathParams && opts.pathParams[0];
      return id ? `/admin/camera-snapshots/${encodeURIComponent(id)}/send-email` : "";
    }
    return "";
  }
}

/** Operator-facing bus / act errors — never paths or stacks. */
export function softCamsErrorOf(err) {
  const n = String(err == null ? "" : err).trim().toLowerCase();
  if (!n) return "That camera action could not be completed.";
  if (/\b403\b|forbidden|can_send|domain_cameras|insufficient|scope/.test(n)) {
    return "Your account cannot do that with cameras. Ask a site manager if you need access.";
  }
  if (/\b401\b|session|unauthorized|missing bearer/.test(n)) {
    return "Sign in again to continue.";
  }
  if (/\b404\b|not found|snapshot_not_found/.test(n)) {
    return "That frame is not available.";
  }
  if (/\b410\b|expired|gone|blob_missing/.test(n)) {
    return "That frame has expired or was cleaned up.";
  }
  if (/\b429\b|too many/.test(n)) {
    return "Too many tries — wait a moment, then retry.";
  }
  if (/\b503\b|product_off|disabled|unavailable/.test(n)) {
    return "Camera capture is not armed on this site right now.";
  }
  if (/destination_not_allowed|ssrf|host rejected|egress/.test(n)) {
    return "That host is not allowed for on-site probe from the server.";
  }
  if (/network|failed to fetch|timeout|aborted/.test(n)) {
    return "Network issue — try Refresh.";
  }
  if (/\/ops\/|\/admin\/|\/devices|http|get |post |status_path|sql|traceback/.test(n)) {
    return "That camera action could not be completed.";
  }
  return "That camera action could not be completed.";
}

export function softCamsSeatLabelOf(role) {
  const r = String(role || "").toLowerCase();
  if (r === "user") return "Operator";
  if (r === "admin") return "Site manager";
  if (r === "superadmin") return "Platform";
  if (r === "sa_staff") return "Staff";
  return role || "Seat";
}

/** Pure: dual seat + 100k limits from Live posture (Page223). */
export function opsCamerasSeatOf(post) {
  const p = post && typeof post === "object" ? post : {};
  const limits = (p.limits && typeof p.limits === "object") ? p.limits : {};
  const framesDefault = Math.min(
    Number(limits.frames_max || 64) || 64,
    Math.max(1, Number(limits.frames_default || 36) || 36),
  );
  const catMax = Number(limits.catalogue_page_max);
  const regMax = Number(limits.register_paint_max);
  const registerAllowed = (p.register_read_allowed === true);
  const framesAllowed = (p.frames_plane_allowed === true || p.frames_allowed === true);
  const catalogueAllowed = (p.catalogue_plane_allowed === true || p.catalogue_allowed === true);
  const adminBus = p.admin_bus_allowed != null
    ? (p.admin_bus_allowed === true)
    : catalogueAllowed;
  return {
    registerAllowed,
    adminBus,
    framesAllowed,
    catalogueAllowed,
    healthAllowed: (p.health_probe_allowed === true),
    probeAllowed: (p.probe_test_allowed === true),
    bindAllowed: (p.bind_enqueue_allowed === true),
    emailAllowed: (p.send_email_allowed === true),
    reReadAllowed: (p.re_read_allowed === true),
    productFlag: p.product_flag === true,
    role: String(p.principal_role || ""),
    detail: p.detail || null,
    enabled: p.enabled === true,
    framesLimit: framesDefault,
    cataloguePaintMax:
      Number.isFinite(catMax) && catMax > 0 ? Math.floor(catMax) : 100,
    registerPaintMax:
      Number.isFinite(regMax) && regMax > 0 ? Math.floor(regMax) : 128,
    isolation: p.isolation || null,
  };
}

/** Soft≠Live protocol labels — never paint raw wire enum as operator DOM. */
export function softCamsProtocolLabelOf(proto) {
  const p = String(proto || "").trim().toLowerCase();
  if (p === "onvif") return "Standard camera"; /* Honesty: ONVIF */
  if (p === "http_snapshot") return "Snapshot URL";
  if (p === "hikvision") return "Hikvision";
  if (p === "dahua") return "Dahua";
  return p ? operatorSay(p, p.replace(/_/g, " ")) : "Standard camera";
}

/** Pure: flatten GET /devices register. */
export function opsCamerasDevicesOf(pack) {
  const items = (pack && (pack.items || pack.devices)) || [];
  const rows = Array.isArray(items) ? items : [];
  const mapped = rows
    .map((r) => {
      if (!r) return null;
      const id = String(r.device_id || r.id || "").trim();
      if (!id) return null;
      const human = String(r.display_label || r.map_label || r.label || "").trim();
      return {
        id,
        label: human || boardHeadingOf(id),
      };
    })
    .filter(Boolean);
  const seen = new Set();
  const out = [];
  for (const d of mapped) {
    if (seen.has(d.id)) continue;
    seen.add(d.id);
    out.push(d);
  }
  return out;
}

function toShots(data) {
  const rows = (data && (data.items || data.snapshots || data.frames)) || [];
  if (!Array.isArray(rows)) return [];
  return rows.map((x, i) => {
    const id = String(x.snapshot_id || x.id || ("s" + i));
    const gone = !!(x.expired || x.gone || x.gone_at || x.is_expired);
    const thumb = String(x.thumb_url || x.image_url || "").trim();
    return {
      id,
      cam: cameraPlantSay(x, i),
      at: String(x.captured_at || x.created_at || x.at || "").slice(0, 19).replace("T", " "),
      gone,
      device_id: x.device_id || "",
      camera_id: x.camera_id || "",
      thumb: thumb && !/^https?:\/\//i.test(thumb) ? thumb : (thumb || ""),
    };
  });
}

export function OpsCamerasDesk({ onShot, toast }) {
  const posture = useApi(safePath("get__ops_cameras_status"));
  const post = posture.data || {};
  const seat = opsCamerasSeatOf(
    posture.loading || !posture.data
      ? {
          register_read_allowed: false,
          frames_plane_allowed: false,
          catalogue_plane_allowed: false,
          re_read_allowed: false,
          product_flag: true,
          limits: {
            frames_default: 36,
            frames_max: 64,
            catalogue_page_max: 100,
            register_paint_max: 128,
          },
        }
      : post,
  );
  const act = useAction();
  const envelope = act.envelope;
  const frames = useApi(
    seat.framesAllowed
      ? safePath("get__ops_cameras_frames", { query: { limit: seat.framesLimit } })
      : "",
  );
  const catalogue = useApi(seat.catalogueAllowed ? safePath("get__admin_cameras") : "");
  // Soft≠Live: always bound plant paint (sa_staff custody ignored limit until honor_limit).
  const devicesR = useApi(
    seat.registerAllowed
      ? safePath("get__devices", {
          query: { limit: seat.registerPaintMax, skip_cache: 1 },
        })
      : "",
  );
  const [deviceId, setDeviceId] = useState("");
  const [probeHost, setProbeHost] = useState("");
  const [probeProto, setProbeProto] = useState("onvif");
  const [selectedShot, setSelectedShot] = useState("");
  const [tab, setTab] = useState("frames");

  const devices = useMemo(() => {
    const rows = opsCamerasDevicesOf(devicesR.data);
    return rows.slice(0, seat.registerPaintMax);
  }, [devicesR.data, seat.registerPaintMax]);
  const shots = useMemo(() => toShots(frames.data), [frames.data]);
  const liveShots = shots.filter((s) => !s.gone);
  const camItems = (catalogue.data && (catalogue.data.items || catalogue.data.cameras)) || [];
  const catDenied = !!(catalogue.error && /\b40[13]\b|owner_admin|tenant|forbidden/i.test(catalogue.error || ""));
  const catOff = !!(catalogue.error && /503|disabled|feature|not.?armed/i.test(catalogue.error || ""));
  const frDenied = !!(frames.error && /\b40[13]\b|forbidden/i.test(frames.error || ""));

  const reloadAll = () => {
    if (!seat.reReadAllowed && !posture.loading) {
      toast && toast("Refresh is closed for this seat", "warn");
      return;
    }
    posture.reload && posture.reload();
    frames.reload && frames.reload();
    catalogue.reload && catalogue.reload();
    devicesR.reload && devicesR.reload();
    toast && toast("Covers refreshed", "go");
  };

  const healthProbe = async (cameraId) => {
    if (!seat.healthAllowed) { toast && toast("Health check is closed for this seat", "warn"); return; }
    if (!cameraId) { toast && toast("Pick a camera first", "warn"); return; }
    const out = await act.run(
      safePath("post__admin_cameras_p_health-probe", { pathParams: [cameraId] }),
      { method: "POST" },
    );
    if (!out) { toast && toast(softCamsErrorOf(act.error), "bad"); return; }
    catalogue.reload && catalogue.reload();
    toast && toast("Check started", "go");
  };

  const probeTest = async () => {
    if (!seat.probeAllowed) { toast && toast("Test is closed for this seat", "warn"); return; }
    if (!probeHost.trim()) { toast && toast("Enter a host to probe", "warn"); return; }
    const out = await act.run(safePath("post__admin_cameras_probe-test"), {
      method: "POST",
      body: {
        host: probeHost.trim(),
        protocol: probeProto || "onvif",
        username: "",
        password: "",
      },
    });
    if (!out) { toast && toast(softCamsErrorOf(act.error), "bad"); return; }
    toast && toast(
      out.ok === false
        ? ("Test refused · " + softCamsErrorOf(out.tag || out.error || "fail"))
        : "Test finished",
      out.ok === false ? "warn" : "go",
    );
  };

  const enqueueSelected = async () => {
    if (!seat.bindAllowed) { toast && toast("Enqueue needs a site-manager seat", "warn"); return; }
    if (!deviceId) { toast && toast("Pick a device first", "warn"); return; }
    await enqueueDeviceSnapshot(act, deviceId, {
      toast,
      reason: "Manual capture from Cams",
    });
    frames.reload && frames.reload();
  };

  const sendEmail = async () => {
    if (!seat.emailAllowed) { toast && toast("E-mail needs a site-manager seat", "warn"); return; }
    if (!selectedShot) { toast && toast("Select a frame first", "warn"); return; }
    const out = await act.run(
      safePath("post__admin_camera-snapshots_p_send-email", { pathParams: [selectedShot] }),
      { method: "POST", body: {} },
    );
    if (!out) { toast && toast(softCamsErrorOf(act.error), "bad"); return; }
    toast && toast("Frame e-mail queued", "go");
  };

  const openShot = (s) => {
    if (s.gone) {
      toast && toast("That frame passed its retention window", "warn");
      return;
    }
    setSelectedShot(s.id);
    onShot && onShot(s);
  };

  const tabOpen = (t) => {
    if (t.needsFrames && !seat.framesAllowed) return false;
    if (t.needsCatalogue && !seat.catalogueAllowed) return false;
    if (t.needsProbe && !seat.probeAllowed) return false;
    if (t.needsBind && !seat.bindAllowed && !seat.registerAllowed) return false;
    return true;
  };

  const catN = Array.isArray(camItems) ? camItems.length : 0;
  const pulseLine = posture.loading
    ? "Reading cameras…"
    : !seat.productFlag
      ? "Camera capture is off on this site"
      : !seat.framesAllowed && !seat.catalogueAllowed
        ? "Cameras closed for this seat."
        : seat.framesAllowed
          ? (liveShots.length
            ? `${liveShots.length} picture${liveShots.length === 1 ? "" : "s"} on site`
            : "No pictures yet")
          : "Camera list only · pictures closed";

  const seatLabel = softCamsSeatLabelOf(seat.role || post.principal_role);

  if (!posture.loading && post.enabled === false) {
    return html`<div
      className="ops-hud ops-cams ops-field ops-field--cams ops-cam ops-cam--liquid"
      data-cmc-ops-cam="page44" data-cmc-page="page101-ops-cameras"
      data-cmc-residual="page223-cameras"
      data-field="v563">
      <header className="ops-hud__top">
        <div className="ops-hud__brand">
          <span className="ops-hud__chip">Cams</span>
          <span className="ops-hud__chip ops-hud__chip--live">Sealed</span>
        </div>
        <button type="button" className="ops-hud__refresh"
          disabled=${!seat.reReadAllowed || posture.loading}
          onClick=${reloadAll} title="Refresh">
          <${Ico} d="refresh" w=${13}/>
        </button>
      </header>
      <p className="ops-hud__pulse">Covers closed</p>
      <p className="ops-hud__quiet">No covers list for this account. Ask a manager if you need access.</p>
    </div>`;
  }

  return html`<div
    className="ops-hud ops-cams ops-field ops-field--cams ops-cam ops-cam--liquid"
    data-cmc-ops-cam="page44" data-cmc-page="page101-ops-cameras"
    data-cmc-residual="page223-cameras"
    data-cmc-cams-frames="v604"
    data-field="v563"
    data-frames=${seat.framesAllowed ? "1" : "0"}
    data-catalogue=${seat.catalogueAllowed ? "1" : "0"}>

    <div className="ops-hud__scan" aria-hidden="true" />

    <header className="ops-hud__top">
      <div className="ops-hud__brand">
        <span className="ops-hud__chip">Cams</span>
        <span className="ops-hud__chip">${seat.framesAllowed || seat.catalogueAllowed ? "On site" : "Sealed"}</span>
      </div>
      <button type="button" className="ops-hud__refresh"
        title="Refresh cameras"
        disabled=${act.busy || frames.loading || catalogue.loading || posture.loading || !seat.reReadAllowed}
        onClick=${reloadAll}>
        <${Ico} d="refresh" w=${13}/>
      </button>
    </header>

    <p className="ops-hud__pulse" title="Covers pulse">${pulseLine}</p>
    ${envelope ? html`<${KV} k="Last action" v=${envelopeSummary(envelope) || "—"} tone=${envelope.ok ? "hi" : "warn"}/>` : null}

    <div className="ops-hud__meters ops-cams__meters" role="group" aria-label="Cover meters">
      <div className="ops-hud__meter" data-on=${seat.framesAllowed ? "1" : "0"} data-tone="go">
        <b>${seat.framesAllowed
          ? (frames.loading && !frames.data ? "…" : liveShots.length)
          : "—"}</b>
        <em>Pictures</em>
        <s className="ops-hud__meter-bar" style=${{
          transform: `scaleX(${seat.framesAllowed && liveShots.length
            ? Math.min(1, Math.max(0.18, liveShots.length / Math.max(1, seat.framesLimit)))
            : 0.18})`,
        }}/>
      </div>
      <div className="ops-hud__meter" data-on=${seat.catalogueAllowed && catN ? "1" : "0"} data-tone="go">
        <b>${seat.catalogueAllowed
          ? (catalogue.loading && !catalogue.data ? "…" : catN)
          : "—"}</b>
        <em>Cameras</em>
        <s className="ops-hud__meter-bar" style=${{
          transform: `scaleX(${seat.catalogueAllowed && catN ? Math.min(1, Math.max(0.18, catN / 12)) : 0.18})`,
        }}/>
      </div>
      <div className="ops-hud__meter" data-on=${seat.registerAllowed && devices.length ? "1" : "0"} data-tone="go">
        <b>${seat.registerAllowed
          ? (devicesR.loading && !devicesR.data ? "…" : devices.length)
          : "—"}</b>
        <em>Boards</em>
        <s className="ops-hud__meter-bar" style=${{
          transform: `scaleX(${seat.registerAllowed && devices.length ? 1 : 0.18})`,
        }}/>
      </div>
      <div className="ops-hud__meter" data-on=${seat.productFlag ? "1" : "0"} data-tone="warn">
        <b>${seatLabel}</b>
        <em>Seat</em>
        <s className="ops-hud__meter-bar" style=${{ transform: "scaleX(0.55)" }}/>
      </div>
    </div>

    ${!seat.productFlag
      ? html`<${Alert} tone="info" title="Capture off"
          say="Camera capture is not armed on this site. A platform admin can enable it."/>`
      : null}
    ${seat.productFlag && !seat.framesAllowed && !seat.catalogueAllowed
      ? html`<${Alert} tone="info" title="Covers closed" say="No pictures or camera list for this seat."/>`
      : null}
    ${seat.framesAllowed && !seat.catalogueAllowed
      ? html`<p className="ops-hud__quiet">You can view pictures for your boards. Adding cameras stays with the site manager.</p>`
      : null}

    <div className="ops-seg ops-cams__tabs" role="tablist" aria-label="Cam cover tabs">
      ${CAM_TABS.map((t) => {
        const open = tabOpen(t);
        return html`
        <button key=${t.id} type="button" role="tab" className="ops-seg__btn ops-cams__tab"
          data-on=${tab === t.id ? "1" : "0"}
          data-dim=${open ? "0" : "1"}
          disabled=${!open}
          title=${open ? (t.long || t.label) : `${t.long || t.label} · sealed for this seat`}
          aria-label=${t.long || t.label}
          onClick=${() => open && setTab(t.id)}>${t.label}</button>`;
      })}
    </div>

    <section className="ops-cams__stage" aria-label="Cam stage">
      ${tab === "frames" ? html`
        <div className="ops-hud__lane-h">
          <p className="ops-hud__k"><s></s>Saved pictures</p>
          <em className="ops-cams__match">≤${seat.framesLimit}</em>
        </div>
        ${!seat.framesAllowed
          ? html`<div className="ops-hud__quiet"><b>Pictures closed</b> · Ask a manager if you need capture history.</div>`
          : frames.loading && !frames.data
            ? html`<p className="ops-hud__quiet">Loading pictures…</p>`
            : frames.error
              ? html`<${Alert} tone=${frDenied ? "info" : "warn"}
                  title=${frDenied ? "Frames gated for this account" : "Could not load frames"}
                  say=${softCamsErrorOf(frames.error)}/>`
              : html`
                <div className="thumbs ops-cam__thumbs ops-cams__thumbs">
                  ${!shots.length
                    ? html`<p className="ops-hud__quiet" style=${{ gridColumn: "1 / -1" }}>No pictures yet. They appear when a camera sends one.</p>`
                    : shots.slice(0, Math.min(18, seat.framesLimit)).map((s) => html`
                        <button key=${s.id} type="button" className="thumb"
                          data-gone=${s.gone ? "1" : "0"}
                          data-on=${selectedShot === s.id ? "1" : "0"}
                          onClick=${() => openShot(s)}>
                          ${s.gone
                            ? html`<span className="gone">expired</span>`
                            : html`<u>${s.cam}</u><s>${s.at}</s>`}
                        </button>`)}
                </div>
                <div className="ops-cams__acts">
                  <button type="button" className="btn btn--ghost btn--sm"
                    disabled=${act.busy || !selectedShot || !seat.emailAllowed}
                    onClick=${sendEmail}
                    title=${seat.emailAllowed ? "E-mail selected frame" : "E-mail needs site manager"}>
                    E-mail selected</button>
                  <em className="ops-cams__match">${liveShots.length} pictures</em>
                </div>`}
      ` : null}

      ${tab === "cat" ? html`
        <div className="ops-hud__lane-h">
          <p className="ops-hud__k"><s></s>Cameras</p>
          <em className="ops-cams__match">${seat.catalogueAllowed ? catN : "closed"}</em>
        </div>
        ${!seat.catalogueAllowed
          ? html`<div className="ops-hud__quiet"><b>Camera list closed</b> · Adding cameras stays with the site manager.</div>`
          : catalogue.loading && !catalogue.data
            ? html`<p className="ops-hud__quiet">Loading cameras…</p>`
            : catalogue.error
              ? html`<${Alert} tone=${catDenied || catOff ? "info" : "warn"}
                  title=${catOff ? "Camera feature not armed"
                    : (catDenied ? "Camera list not on this seat" : "Could not load cameras")}
                  say=${softCamsErrorOf(catalogue.error)}/>`
              : !camItems.length
                ? html`<div className="ops-hud__quiet"><b>No cameras yet</b> · Add them under Settings → Cameras, or link from a board.</div>`
                : html`<div className="ops-cams__list" role="list">
                    ${camItems.slice(0, Math.min(24, seat.cataloguePaintMax)).map((c, i) => {
                      const id = c.camera_id || c.id;
                      return html`
                        <div key=${id} className="ops-cams__row" role="listitem">
                          <span className="ops-cams__main">
                            <b>${cameraPlantSay(c, i)}</b>
                            <u>${operatorSay(c.health_status || c.status, "On this site")}</u>
                          </span>
                          <button type="button" className="btn btn--ghost btn--xs"
                            disabled=${act.busy || !id || !seat.healthAllowed}
                            onClick=${() => healthProbe(id)}>Check</button>
                        </div>`;
                    })}
                  </div>`}
      ` : null}

      ${tab === "probe" ? html`
        <div className="ops-hud__lane-h">
          <p className="ops-hud__k"><s></s>Test a camera</p>
          <em className="ops-cams__match">does not add it yet</em>
        </div>
        ${!seat.probeAllowed
          ? html`<div className="ops-hud__quiet"><b>Test closed</b> · A site manager can try a camera on the network without adding it.</div>`
          : html`<div className="ops-cams__form">
              <${Field} label="Host">
                <${Input} icon="chip" value=${probeHost}
                  onChange=${(e) => setProbeHost(e.target.value)}
                  placeholder="camera on your LAN"/>
              <//>
              <${Field} label="How to reach it">
                <select className="inp" value=${probeProto}
                  onChange=${(e) => setProbeProto(e.target.value)}
                  aria-label="Camera reach protocol">
                  ${["onvif", "http_snapshot", "hikvision", "dahua"].map((p) => html`
                    <option key=${p} value=${p}>${softCamsProtocolLabelOf(p)}</option>`)}
                </select>
              <//>
              <button type="button" className="btn btn--go btn--sm"
                disabled=${act.busy || !probeHost.trim()}
                onClick=${probeTest}>
                ${act.busy ? "Checking…" : "Check host"}</button>
              <p className="ops-hud__quiet">This only checks the camera. It does not add it to the list.</p>
            </div>`}
      ` : null}

      ${tab === "bind" ? html`
        <div className="ops-hud__lane-h">
          <p className="ops-hud__k"><s></s>Link camera</p>
          <em className="ops-cams__match">pick a board</em>
        </div>
        <div className="ops-cams__form">
          <${Field} label="Board">
            <select className="inp" value=${deviceId}
              onChange=${(e) => setDeviceId(e.target.value)}
              disabled=${!seat.registerAllowed}>
              <option value="">— choose a board —</option>
              ${devices.map((d) => html`
                <option key=${d.id} value=${d.id}>${d.label || boardHeadingOf(d.id)}</option>`)}
            </select>
          <//>
          <div className="ops-cams__acts">
            <button type="button" className="btn btn--go btn--sm"
              disabled=${act.busy || !deviceId || !seat.bindAllowed}
              onClick=${enqueueSelected}
              title=${seat.bindAllowed ? "Enqueue capture" : "Needs a site manager"}>
              ${act.busy ? "Working…" : "Take picture"}</button>
            ${deviceId ? html`
              <button type="button" className="btn btn--ghost btn--sm"
                onClick=${() => go("/machine/" + encodeURIComponent(deviceId))}>
                Open full board</button>` : null}
          </div>
          ${!seat.registerAllowed
            ? html`<p className="ops-hud__quiet">Board list closed for this seat.</p>`
            : !devices.length
              ? html`<p className="ops-hud__quiet">No boards yet on this account.</p>`
              : null}
          ${!seat.bindAllowed
            ? html`<p className="ops-hud__quiet">Linking cameras stays with the site manager. You can still open pictures for boards you can see.</p>`
            : deviceId
              ? html`<${DeviceCamerasLive} deviceId=${deviceId} toast=${toast}/>`
              : html`<p className="ops-hud__quiet">Choose a board to link a camera.</p>`}
        </div>
      ` : null}
    </section>

    ${act.error
      ? html`<${Alert} tone="bad" title="Last write refused" say=${softCamsErrorOf(act.error)}/>`
      : null}

    <footer className="ops-hud__foot ops-cams__foot">
      <span className="ops-hud__foot-role">Covers</span>
      <button type="button" className="btn btn--ghost btn--xs" onClick=${() => go("/cameras/live")}>Live wall</button>
      <button type="button" className="btn btn--ghost btn--xs" onClick=${() => go("/settings/cameras")}>Settings</button>
    </footer>
  </div>`;
}

export default OpsCamerasDesk;
